import { uniq } from 'lodash/fp';

import type {
  CombinedRunStepForTree,
  CombinedStep,
  TreeNode,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { InquiryTemplateVersionStep } from 'dashboard/models/inquiry-template-version';
import { WorkflowStepType } from 'dashboard/models/workflow-version';
import { RunStepTreeConfig } from 'dashboard/redux/slices/inquiry-run';
import { UiStepType } from 'lib/interfaces/StepsConfig';
import { assertNever } from 'lib/utils/types';

type BuildInspectTreeParams = {
  initialStep: InquiryTemplateVersionStep | null;
  currentStepName: string | null;
  previousStepName: string | null;
  combinedSteps: CombinedStep[];
  graph: Record<string, TreeNode>;
};

const getGraphStepNameForLoopStep = (
  graph: Record<string, TreeNode>,
  loopStepName: string,
): string | null => {
  const loopStep = Object.values(graph)
    .map((node) => node.step)
    .find(
      (step) =>
        step.type === UiStepType.Loop &&
        step.config.steps.filter((s) => s.name === loopStepName).length > 0,
    );
  if (!loopStep) {
    return null;
  }
  return loopStep.token;
};

export const buildInspectTree = ({
  initialStep,
  currentStepName,
  previousStepName,
  combinedSteps,
  graph,
}: BuildInspectTreeParams): CombinedRunStepForTree[] => {
  if (initialStep == null) {
    return [];
  }
  const treeRootStep = combinedSteps.find(
    (step) => step.token === initialStep.name,
  ) as CombinedStep;
  if (previousStepName && !graph[previousStepName]) {
    previousStepName = getGraphStepNameForLoopStep(graph, previousStepName);
  }
  if (currentStepName && !graph[currentStepName]) {
    currentStepName = getGraphStepNameForLoopStep(graph, currentStepName);
  }

  return buildTree(
    treeRootStep,
    graph,
    buildRunStepTreeConfigs(graph, currentStepName, previousStepName),
  );
};

const buildRunStepTreeConfigs = (
  graph: Record<string, TreeNode>,
  currentStepName: string | null,
  previousStepName: string | null,
): RunStepTreeConfig[] => {
  if (currentStepName == null) {
    return [];
  }

  if (previousStepName == null) {
    return [
      {
        token: currentStepName,
        prevToken: '',
      },
    ];
  }

  return buildRunStepTreeConfigsRecursive(graph, previousStepName, null, currentStepName);
};

const buildRunStepTreeConfigsRecursive = (
  graph: Record<string, TreeNode>,
  stepToken: string,
  prevStepToken: string | null,
  targetStepToken: string,
): RunStepTreeConfig[] => {
  const stepNode = graph[stepToken];

  if (stepNode.step.token === targetStepToken) {
    // We reached the end of the intended path
    return [
      {
        token: stepToken,
        // This branch should not be reached with null prevStepToken but just in case
        prevToken: prevStepToken || '',
      },
    ];
  }

  const children = stepNode.children;

  if (children.length === 0) {
    // We visit a step that has no children and thus traversed a path that does not
    // end in the target step. Return empty array to represent no RunStepTreeConfig
    // is this path
    return [];
  } else {
    const possiblePaths = children.map((childToken, idx) => {
      const childNode = graph[childToken];
      if (childNode.step.type !== 'workflow' && childNode.step.token !== targetStepToken) {
        // We visited a visual step and thus traversed a path that does not end in the
        // target step. Return empty array to represent no RunStepTreeConfig is this path
        return {
          [idx]: [],
        };
      }

      return {
        [idx]: buildRunStepTreeConfigsRecursive(graph, childToken, stepToken, targetStepToken),
      };
    });

    const chosenIdx = possiblePaths.findIndex((path, idx) => path[idx].length > 0);

    if (chosenIdx === -1) {
      return [];
    }

    const chosenPathMap = possiblePaths[chosenIdx];

    return [
      ...(prevStepToken == null
        ? []
        : [
            {
              token: stepToken,
              prevToken: prevStepToken,
              chosenPathMap,
            },
          ]),
      ...chosenPathMap[chosenIdx],
    ];
  }
};

const buildTree = (
  step: CombinedStep,
  graph: Record<string, TreeNode>,
  runStepTreeConfigs: RunStepTreeConfig[],
) => buildTreeRecursive(new Set<string>(), null, step, graph, runStepTreeConfigs);

const buildTreeRecursive = (
  visited: Set<string>,
  prevStep: CombinedStep | null,
  step: CombinedStep,
  graph: Record<string, TreeNode>,
  runStepTreeConfigs: RunStepTreeConfig[],
): CombinedRunStepForTree[] => {
  // Find the event that represents the transition from `prevStep` to `step`.
  const matchingRunStepTreeConfig = runStepTreeConfigs?.find(
    (runStepTreeConfig) =>
      runStepTreeConfig.token === step.token && runStepTreeConfig.prevToken === prevStep?.token,
  );

  const isInRunPath = matchingRunStepTreeConfig != null;
  const chosenIndexes = Object.keys(matchingRunStepTreeConfig?.chosenPathMap ?? {}).map((index) =>
    Number(index),
  );

  /**
   * return just the current step if step has already been visited.
   * avoids duplicate subtrees, instead renders a reference to an existing screen (connector)
   */
  if (visited.has(step.token)) {
    return [
      {
        ...step,
        isConnector: true,
        isInRunPath,
      },
    ];
  }

  visited.add(step.token);
  const hasBranches = graph[step.token].children.length > 1;

  /**
   * UiStepType.Complete is a non-UI terminal step. For all other step types, return:
   * - step + recursive remainder of branch OR
   * - step with branches (+recursive branch paths)
   */
  switch (step.type) {
    case UiStepType.Complete:
      return [{ ...step, isInRunPath }];
    case UiStepType.Document:
    case UiStepType.Integration:
    case UiStepType.VerifyWithPersona:
    case UiStepType.Selfie:
    case UiStepType.SelfieReflection:
    case UiStepType.GovernmentId:
    case UiStepType.Ui:
    case UiStepType.Loop: {
      /**
       * Construct chosenPathMap in-place here rather than in getRunStepTreeConfigs() because
       * screen step branching is often non-sequential and can lead to transition events being
       * dropped or out of order (examples: KYB flows, Document home)
       */
      const chosenPathMap: Record<number, { tokenIndex: number; prevToken: string }> = uniq(
        graph[step.token].children,
      ).reduce((map, child, i) => {
        const tokenIndex = (runStepTreeConfigs ?? []).findIndex(
          (config) =>
            config.token.includes(graph[child].step.token) &&
            // pass configs if current step directly transitions to a subsequent config (child)
            // OR if a subsequent config exists on the remaining path (connector reference)
            (config.prevToken.includes(step.token) || !visited.has(graph[child].step.token)),
        );
        return tokenIndex !== -1
          ? {
              ...map,
              [i]: {
                tokenIndex,
                prevToken: runStepTreeConfigs[tokenIndex].prevToken,
              },
            }
          : map;
      }, {});
      /**
       * filter indexes that do not directly continue from the current step, as
       * the connector should not be highlighted even if the subsequent step
       * is on the run path (e.g reached via a connector)
       */
      const chosenIndexes = Object.keys(chosenPathMap)
        .map((index) => {
          const { tokenIndex, prevToken } = chosenPathMap[Number(index)];
          return tokenIndex === 1 || prevToken.includes(step.token) ? Number(index) : null;
        })
        .filter((val): val is number => val != null);
      /**
       * branches for UI steps occur when:
       * - the step has multiple buttons OR
       * - a button on the step transitions to a workflow, as the
       *   workflow_on_error_step_name attribute is then required
       */
      return hasBranches
        ? [
            {
              ...step,
              chosenIndexes,
              // set isInRunPath to true if chosen indexes are found in case this step's
              // transition event was dropped, but subsequent valid runStepConfigs exist
              isInRunPath,
              branches: graph[step.token].children.map((node) => {
                return buildTreeRecursive(
                  visited,
                  step,
                  graph[node].step,
                  graph,
                  runStepTreeConfigs,
                );
              }),
            },
          ]
        : [
            // set isInRunPath to true if subsequent runStepConfigs remain in case this step's
            // transition event was dropped. only slice array if step is confirmed to be on run path
            {
              ...step,
              isInRunPath,
            },
            ...graph[step.token].children.flatMap((node) =>
              buildTreeRecursive(visited, step, graph[node].step, graph, runStepTreeConfigs),
            ),
          ];
    }
    case 'workflow': {
      /**
       * workflowStepType is added to the node for stronger typing.
       * update_next_step actions are omitted, as these aren't rendered in the InquiryRunTree.
       */
      const currentStep = {
        ...step,
        workflowStepType: step.workflowStep?.type,
      };
      const skipCurrentStep = step.workflowStep.isUpdateNextStepAction;
      return step.workflowStep?.type === WorkflowStepType.Conditional
        ? [
            {
              ...currentStep,
              // set isInRunPath to true if chosen indexes are found in case this step's
              // transition event was dropped, but subsequent valid runStepConfigs exist
              isInRunPath,
              chosenIndexes,
              // map with step.workflowStep.branches over graph[step.token].children to support empty branches
              branches: step.nextSteps.map(
                (nextStepToken) =>
                  nextStepToken != null
                    ? buildTreeRecursive(
                        visited,
                        step,
                        graph[nextStepToken].step,
                        graph,
                        runStepTreeConfigs,
                      )
                    : [], // still want to display a branch even if its empty
              ),
            },
          ]
        : [
            ...(skipCurrentStep
              ? []
              : /**
                 * actions nodes must be in the run path if runStepTreeConfigs is non-empty because
                 * even though they are not explicitly identified by Inquiry run events,
                 * the prev/next nodes must be screen or branch nodes which are.
                 */
                [{ ...currentStep, isInRunPath }]),
            ...graph[step.token].children.flatMap((node) =>
              buildTreeRecursive(visited, step, graph[node].step, graph, runStepTreeConfigs),
            ),
          ];
    }
    default:
      assertNever(step);
  }
};
